import { TFunction } from 'react-i18next';
import { MCPServerSettings } from './Settings';
import { MCPServerTools } from './Tools';
import { MCPServerTeamSharingSettings } from './TeamSharingSettings';
import { MCPServerTimeline } from './Timeline';
import type { RouteConfig } from '__components/Navigation/SubNavigation/types';

type Page = 'settings' | 'tools' | 'teamSharing' | 'timeline';

const routeBase = `/mcp-servers/:mcpServerId`;

const buildRoutes = (baseUrl: string): Record<Page, RouteConfig> => ({
  settings: {
    label: (t: TFunction) => t('MCP Server Settings'),
    component: MCPServerSettings,
    exact: true,
    icon: 'cogs-light',
    path: baseUrl,
  },
  tools: {
    label: (t: TFunction) => t('Tools'),
    component: MCPServerTools,
    exact: true,
    icon: 'tools-light',
    path: `${baseUrl}/tools`,
  },
  teamSharing: {
    label: (t: TFunction) => t('Team Sharing Settings'),
    component: MCPServerTeamSharingSettings,
    exact: true,
    icon: 'share-alt',
    path: `${baseUrl}/team-sharing`,
  },
  timeline: {
    label: (t: TFunction) => t('Timeline'),
    component: MCPServerTimeline,
    exact: true,
    icon: 'timeline-arrow-light',
    path: `${baseUrl}/timeline`,
  },
});

export const getMCPServerDetailSubroutes = (
  mcpServerId: string
): Record<Page, RouteConfig> => buildRoutes(`/mcp-servers/${mcpServerId}`);

export const getMCPServerDetailRoutePatterns = (): Record<Page, RouteConfig> =>
  buildRoutes(routeBase);

const WRITE_ONLY_PAGES: Page[] = ['teamSharing', 'timeline'];

const getVisiblePages = (canWrite: boolean): Page[] =>
  (['settings', 'tools', 'teamSharing', 'timeline'] as Page[]).filter(
    (page) => canWrite || !WRITE_ONLY_PAGES.includes(page)
  );

export const getVisibleMCPServerDetailSubroutes = (
  mcpServerId: string,
  canWrite: boolean
): RouteConfig[] => {
  const routes = getMCPServerDetailSubroutes(mcpServerId);
  return getVisiblePages(canWrite).map((page) => routes[page]);
};

export const getVisibleMCPServerDetailRoutePatterns = (
  canWrite: boolean
): RouteConfig[] => {
  const routes = getMCPServerDetailRoutePatterns();
  return getVisiblePages(canWrite).map((page) => routes[page]);
};
